import { useState } from 'react';
import { StyleProp, ViewStyle } from 'react-native';
import { RoundIconButton } from '@shared/components/MBButton';
import { MBView } from '@shared/components/MBView';
import { grid } from '@shared/styles';
import { useMountedRef } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { COPY_TOOLTIP_DURATION_MS } from './constants';

interface UrlActionButtonsProps {
  url: string;
  showCopy?: boolean;
  showOpen?: boolean;
  style?: StyleProp<ViewStyle>;
}

export function UrlActionButtons({
  url,
  showCopy = true,
  showOpen = true,
  style,
}: UrlActionButtonsProps) {
  const mounted = useMountedRef();
  const initialCopyTooltip = i18n('workflowTemplate.component.url.actionButtons.copyToClipboard');
  const copiedTooltip = i18n('workflowTemplate.component.url.actionButtons.copiedToClipboard');
  const [copyTooltip, setCopyTooltip] = useState(initialCopyTooltip);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopyTooltip(copiedTooltip);
      setTimeout(() => {
        if (mounted.current) {
          setCopyTooltip(initialCopyTooltip);
        }
      }, COPY_TOOLTIP_DURATION_MS);
    } catch (err) {
      // Fallback for browsers that don't support navigator.clipboard
      console.error('Failed to copy to clipboard', err);
    }
  };

  const openUrl = () => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const shouldRender = showCopy || showOpen;
  if (!shouldRender) {
    return null;
  }

  return (
    <MBView row centerVertically style={style}>
      {showCopy && (
        <RoundIconButton
          icon="squares-stacked"
          onPress={copyToClipboard}
          iconSize={grid(2.75)}
          tooltip={copyTooltip}
        />
      )}
      {showOpen && (
        <RoundIconButton
          icon="arrow-link-out"
          onPress={openUrl}
          style={{ marginLeft: grid(0.5) }}
          tooltip={i18n('workflowTemplate.component.url.actionButtons.openUrl', { url })}
        />
      )}
    </MBView>
  );
}
